import * as React from 'react';
import { styled } from 'styled-components';
import kellyPartnersLogoBlueSvg from '../../images/kellyPartnersLogoBlue.svg';
import Helmet from '../../commonComponents/Helmet';
import { tabletPortraitMinWidth } from '../cssConstants';
import { getAppDisplayName } from '../../core/appType';

const FULL_PRIVACY_POLICY_URL = 'https://www.kellypartners.com.au/privacy-policy';

const AppPrivacyPolicyPage = () => {
	const appName = getAppDisplayName();

	return (
		<PageWrapper>
			<Helmet>
				<title>App Privacy Policy</title>
			</Helmet>
			<ContentContainer>
				<HeaderWrapper>
					<HeaderLogo src={kellyPartnersLogoBlueSvg} alt="Kelly+Partners Logo" />
				</HeaderWrapper>

				<Title>{appName} - App Privacy Policy</Title>
				<Subtitle>Supplementary Privacy Disclosures for Mobile Application</Subtitle>

				<Intro>
					This page supplements the{' '}
					<ExternalLink href={FULL_PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer">
						Kelly+Partners Group Privacy Policy
					</ExternalLink>
					. It provides additional detail on how the {appName} mobile application collects, uses, stores, and retains
					personal information, including health and fitness data. In the event of any inconsistency, this supplementary
					disclosure prevails to the extent of the inconsistency.
				</Intro>

				<SectionTitle>1. Account and Usage Data</SectionTitle>
				<SectionContent>
					We retain personal information collected through our mobile applications only for as long as necessary to
					provide our services, or as required by law. This includes:
				</SectionContent>
				<List>
					<li>
						<strong>Account and profile data</strong> (name, email, office affiliation), which is retained for 7 years
						following account closure in line with Australian financial recordkeeping obligations.
					</li>
					<li>
						<strong>Usage, analytics, and diagnostic data</strong>, which is retained for up to 24 months.
					</li>
				</List>
				<SectionContent>
					You may request deletion of your data at any time by contacting us or by deleting your account within the
					app&rsquo;s side menu.
				</SectionContent>

				<SectionTitle>2. Health and Fitness Data</SectionTitle>
				<SectionContent>
					Our mobile application accesses your physical activity data (specifically <strong>step counts</strong>) from
					your device via Apple HealthKit (iOS) or Google Health Connect (Android). We access this data only with your
					explicit permission.
				</SectionContent>

				<SubSectionTitle>What we collect</SubSectionTitle>
				<List>
					<li>
						Your <strong>daily step count</strong>, read from your device&rsquo;s health platform.
					</li>
				</List>

				<SubSectionTitle>How we use it</SubSectionTitle>
				<List>
					<li>To display your daily step progress and goal tracking within the app.</li>
					<li>
						To calculate and store your <strong>monthly total step count</strong> in our database.
					</li>
					<li>
						To display your monthly step total and display name on the in-app{' '}
						<strong>&ldquo;Monthly Leaderboard&rdquo;</strong>, which is visible to other registered users of the
						application.
					</li>
				</List>

				<SubSectionTitle>How we store and retain it</SubSectionTitle>
				<List>
					<li>
						Monthly step totals are stored securely in our database hosted on Microsoft Azure, protected by
						industry-standard encryption at rest and in transit.
					</li>
					<li>
						Step data is retained for as long as your account is active. Upon account deletion, your health data is
						removed from the leaderboard and deleted from our systems within 90 days.
					</li>
				</List>

				<SubSectionTitle>What we do not do</SubSectionTitle>
				<List>
					<li>We do not share your health data with third-party advertisers or external health platforms.</li>
					<li>We do not sell your health data.</li>
					<li>We do not use your health data for advertising or marketing purposes.</li>
					<li>
						We do not access health data beyond step counts (we do not read heart rate, sleep, medical records, or
						other sensitive health information).
					</li>
				</List>

				<SectionTitle>3. Your Rights</SectionTitle>
				<SectionContent>You have the right to:</SectionContent>
				<List>
					<li>Revoke health data permissions at any time through your device settings.</li>
					<li>Request deletion of your stored health data by contacting us.</li>
					<li>
						Opt out of the Monthly Leaderboard by revoking health data permissions (your data will no longer be
						collected or displayed).
					</li>
				</List>

				<SectionTitle>4. Contact Us</SectionTitle>
				<SectionContent>
					For questions about how we handle your data, including health and fitness data, please contact the
					Kelly+Partners Privacy Officer by email at{' '}
					<ExternalLink href="mailto:info@kellypartners.com.au">info@kellypartners.com.au</ExternalLink>.
				</SectionContent>

				<Divider />

				<Footer>
					For full details on how Kelly+Partners Group Holdings Limited handles your personal information, please refer
					to our{' '}
					<ExternalLink href={FULL_PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer">
						complete Privacy Policy
					</ExternalLink>
					.
				</Footer>

				<LastUpdated>Last updated: March 2026</LastUpdated>
			</ContentContainer>
		</PageWrapper>
	);
};

export default AppPrivacyPolicyPage;

const PageWrapper = styled.div`
	min-height: 100vh;
	background-color: #f9fafb;
	padding: 20px 16px 60px;

	/* Allow text selection on this page for legal content */
	-webkit-user-select: text !important;
	user-select: text !important;

	* {
		-webkit-user-select: text !important;
		user-select: text !important;
	}
`;

const ContentContainer = styled.div`
	max-width: 720px;
	margin: 0 auto;
	background: #ffffff;
	border-radius: 12px;
	padding: 32px 24px;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

	@media (min-width: ${tabletPortraitMinWidth}) {
		padding: 48px 40px;
	}
`;

const HeaderWrapper = styled.div`
	text-align: center;
	margin-bottom: 32px;
`;

const HeaderLogo = styled.img`
	height: auto;
	width: 200px;

	@media (min-width: ${tabletPortraitMinWidth}) {
		width: 280px;
	}
`;

const Title = styled.h1`
	font-size: 24px;
	font-weight: 700;
	color: #111827;
	margin: 0 0 8px;
	text-align: center;

	@media (min-width: ${tabletPortraitMinWidth}) {
		font-size: 28px;
	}
`;

const Subtitle = styled.p`
	font-size: 14px;
	color: #6b7280;
	margin: 0 0 24px;
	text-align: center;
`;

const Intro = styled.p`
	font-size: 15px;
	line-height: 1.7;
	color: #374151;
	margin: 0 0 32px;
`;

const SectionTitle = styled.h2`
	font-size: 18px;
	font-weight: 600;
	color: #111827;
	margin: 28px 0 12px;

	@media (min-width: ${tabletPortraitMinWidth}) {
		font-size: 20px;
	}
`;

const SubSectionTitle = styled.h3`
	font-size: 15px;
	font-weight: 600;
	color: #374151;
	margin: 16px 0 8px;
`;

const SectionContent = styled.p`
	font-size: 15px;
	line-height: 1.7;
	color: #374151;
	margin: 0 0 12px;
`;

const List = styled.ul`
	font-size: 15px;
	line-height: 1.7;
	color: #374151;
	margin: 0 0 16px;
	padding-left: 24px;

	li {
		margin-bottom: 6px;
	}
`;

const ExternalLink = styled.a`
	color: #005e9e;
	text-decoration: underline;

	&:hover {
		color: #008aec;
	}
`;

const Divider = styled.hr`
	border: none;
	border-top: 1px solid #e5e7eb;
	margin: 32px 0;
`;

const Footer = styled.p`
	font-size: 15px;
	line-height: 1.7;
	color: #374151;
	margin: 0 0 16px;
`;

const LastUpdated = styled.p`
	font-size: 13px;
	color: #9ca3af;
	margin: 0;
	text-align: center;
`;
